import React from 'react';
import { DefaultButton, PrimaryButton, Modal } from '@fluentui/react';
import styles from './ConfirmActionDialog.module.css';

export interface ConfirmActionDialogProps {
  isOpen: boolean;
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  isDestructive?: boolean;
  isLoading?: boolean;
  errorMessage?: string | null;
  requireText?: string;
  requireTextLabel?: string;
  typedText?: string;
  onTypedTextChange?: (value: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

export const ConfirmActionDialog: React.FC<ConfirmActionDialogProps> = ({
  isOpen,
  title,
  body,
  confirmLabel,
  cancelLabel = 'Cancel',
  isDestructive = true,
  isLoading = false,
  errorMessage,
  requireText,
  requireTextLabel,
  typedText = '',
  onTypedTextChange,
  onConfirm,
  onCancel,
}) => {
  const requiresMatch = Boolean(requireText);
  const confirmDisabled = isLoading || (requiresMatch && typedText.trim() !== requireText);

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={isLoading ? undefined : onCancel}
      isBlocking
      containerClassName={styles.modal}
    >
      <div className={styles.header}>
        <h2>{title}</h2>
      </div>
      <div className={styles.body}>{body}</div>
      {requiresMatch && (
        <div className={styles.confirmInput}>
          <label>{requireTextLabel || `Type ${requireText} to confirm`}</label>
          <input
            type="text"
            value={typedText}
            onChange={(event) => onTypedTextChange?.(event.target.value)}
            placeholder={requireText}
            disabled={isLoading}
          />
        </div>
      )}
      {errorMessage && <div className={styles.error}>{errorMessage}</div>}
      <div className={styles.actions}>
        <PrimaryButton
          text={isLoading ? 'Processing...' : confirmLabel}
          onClick={onConfirm}
          disabled={confirmDisabled}
          styles={isDestructive ? { root: { backgroundColor: '#b4232c', borderColor: '#b4232c' } } : undefined}
        />
        <DefaultButton text={cancelLabel} onClick={onCancel} disabled={isLoading} />
      </div>
    </Modal>
  );
};
